import { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Building2 } from 'lucide-react';
import { Modal, Button } from '../ui';
import { cn } from '../../lib/utils';
import { getTenants, createTenantNote, TENANT_NOTE_CATEGORY_OPTIONS } from '../../services';
import type { Tenant, TenantNote, TenantNoteCategory } from '../../data/types';

interface QuickCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When provided, the note is scoped to this tenant and the picker is hidden. */
  tenantId?: string;
  tenantName?: string;
  /** Optional incident backreference (set from the incident detail page). */
  incidentId?: string | null;
  onSaved?: (note: TenantNote) => void;
}

/**
 * Shared quick-capture composer for Customer Notebook notes. Reused by the
 * global header button (picker mode), the Notebook page (locked tenant), and
 * the incident detail page (locked tenant + incident backreference).
 */
export function QuickCaptureModal({
  isOpen,
  onClose,
  tenantId,
  tenantName,
  incidentId,
  onSaved,
}: QuickCaptureModalProps) {
  const locked = !!tenantId;

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(tenantId ?? null);
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(tenantName ?? null);
  const [tenantQuery, setTenantQuery] = useState('');
  const [pickingTenant, setPickingTenant] = useState(false);
  const [category, setCategory] = useState<TenantNoteCategory | ''>('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset form each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedTenantId(tenantId ?? null);
    setSelectedTenantName(tenantName ?? null);
    setTenantQuery('');
    setPickingTenant(!tenantId);
    setCategory('');
    setContent('');
    setError(null);
    setSaving(false);
    const t = setTimeout(() => textareaRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [isOpen, tenantId, tenantName]);

  // Lazy-load the tenant list for picker mode.
  useEffect(() => {
    if (isOpen && !locked && tenants.length === 0) {
      getTenants().then((r) => {
        if (r.success) setTenants(r.data);
      });
    }
  }, [isOpen, locked, tenants.length]);

  const filteredTenants = useMemo(() => {
    const q = tenantQuery.trim().toLowerCase();
    const list = q
      ? tenants.filter(
          (t) =>
            t.displayName.toLowerCase().includes(q) || t.tenantId.toLowerCase().includes(q)
        )
      : tenants;
    return list.slice(0, 8);
  }, [tenants, tenantQuery]);

  const handleSave = async () => {
    const trimmed = content.trim();
    if (!selectedTenantId) {
      setError('Select a customer first');
      return;
    }
    if (!trimmed) {
      setError('Write a note first');
      return;
    }
    setSaving(true);
    setError(null);
    const result = await createTenantNote(selectedTenantId, {
      content: trimmed,
      category: category || null,
      incidentId: incidentId ?? null,
    });
    setSaving(false);
    if (!result.success) {
      setError(result.error || 'Failed to save note');
      return;
    }
    onSaved?.(result.data);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Quick note"
      description={incidentId ? 'Linked to this incident' : undefined}
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="hidden sm:block text-xs text-text-muted">&#8984;/Ctrl + Enter to save</span>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || !content.trim() || !selectedTenantId}
            >
              {saving ? 'Saving…' : 'Save note'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Tenant selector */}
        {locked ? (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-text-muted" />
            <span className="font-medium text-text-primary">
              {selectedTenantName || selectedTenantId}
            </span>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Customer</label>
            {selectedTenantId && !pickingTenant ? (
              <button
                type="button"
                onClick={() => {
                  setPickingTenant(true);
                  setTenantQuery('');
                }}
                className="w-full flex items-center justify-between px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-sm text-text-primary"
              >
                <span className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-text-muted" />
                  {selectedTenantName || selectedTenantId}
                </span>
                <span className="text-xs text-text-muted">Change</span>
              </button>
            ) : (
              <>
                <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg">
                  <Search className="w-4 h-4 text-text-muted" />
                  <input
                    autoFocus
                    value={tenantQuery}
                    onChange={(e) => setTenantQuery(e.target.value)}
                    placeholder="Search customers…"
                    className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-muted focus:outline-none"
                  />
                </div>
                <div className="mt-1 max-h-48 overflow-y-auto border border-border-default rounded-lg divide-y divide-border-default">
                  {filteredTenants.length === 0 ? (
                    <div className="px-3 py-3 text-sm text-text-muted">No customers found</div>
                  ) : (
                    filteredTenants.map((t) => (
                      <button
                        key={t.tenantId}
                        type="button"
                        onClick={() => {
                          setSelectedTenantId(t.tenantId);
                          setSelectedTenantName(t.displayName);
                          setPickingTenant(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-bg-tertiary transition-colors"
                      >
                        <Building2 className="w-4 h-4 text-text-muted flex-shrink-0" />
                        <span className="text-text-primary truncate">{t.displayName}</span>
                        <span className="ml-auto text-xs text-text-muted">{t.tenantId}</span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Category */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">
            Category <span className="text-text-muted">(optional)</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as TenantNoteCategory | '')}
            className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue"
          >
            <option value="">No category</option>
            {TENANT_NOTE_CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Content */}
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Note</label>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={5}
            placeholder="What happened, or what did we do for this customer this month…"
            className={cn(
              'w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-sm',
              'text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none'
            )}
          />
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </Modal>
  );
}
